import { CustomDropdown } from "@/modules/common/components/ui/custom-drop-down";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { muiTheme } from "@/styles/theme";
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

import { MoreVertical } from "lucide-react";
import React, { useMemo } from "react";
import SingleSelectSearch from "@/modules/common/components/input/single-autocomplete-search";
import { getQuarter } from "date-fns";
import MonthlySummary from "./actions/monthly-summary";

import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import OrganisationsActions from "@/modules/common/components/organizations/organisations-actions";
import { useUsersWithTargets } from "@/modules/targetsv2/hooks/users-with-targets";
import ViewQuarterlyTarget from "@/modules/targetsv2/components/targets-actions/actions/view-quarterly-target";
import OpenViewQuartelyTarget from "@/modules/targetsv2/components/targets-actions/actions/open-view-quarterly-target";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";

const years = Array.from(
  { length: new Date().getFullYear() - 2015 },
  (_, i) => 2016 + i
);

const quartersData: {
  id: 1 | 2 | 3 | 4;
  name: string;
}[] = [
  { id: 1, name: "Q1" },
  { id: 2, name: "Q2" },
  { id: 3, name: "Q3" },
  { id: 4, name: "Q4" },
];

const TargetsActions = ({
  selectedUserUId,
  handleUserChange,
  actionsRef,
  selectedYear,
  handleYearChange,
  selectedQuarter,
  handleQuarterChange,
}: {
  selectedUserUId: string | null;
  handleUserChange: (userUId: string | null) => void;
  actionsRef: React.RefObject<HTMLDivElement>;
  selectedYear: number;
  handleYearChange: (year: number) => void;
  selectedQuarter: 1 | 2 | 3 | 4;
  handleQuarterChange: (quarter: 1 | 2 | 3 | 4) => void;
}) => {
  const quarters = quartersData.filter((q) => {
    if (selectedYear === new Date().getFullYear()) {
      return q.id <= getQuarter(new Date());
    }
    return true;
  });

  const { isMobile } = useScreenSize();

  const currentQuarter = useMemo(() => getQuarter(new Date()), []);
  const currentYear = new Date().getFullYear();

  const isCurrentQuarter =
    currentQuarter === selectedQuarter && currentYear === selectedYear;

  const triggerButton = (
    handleClick: (event: React.MouseEvent<HTMLElement>) => void
  ) => (
    <IconButton
      sx={{
        backgroundColor:
          muiTheme.colorSchemes.light.palette.main.actionDisabled,
        borderRadius: 1,
        boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
      }}
      onClick={handleClick}
    >
      <MoreVertical size={24} />
    </IconButton>
  );

  const {
    usersData: users,
    setUsersSearchTerm,
    usersSearchTerm,
    isLoading: usersLoading,
  } = useUsersWithTargets({
    year: selectedYear,
    quarter: selectedQuarter,
  });

  const {
    renderOrganisationsActions,
    selectedSingleOrganization,
    showSingleOrganizationTables,
  } = useOrganization();
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        padding: isMobile ? "16px" : "32px 16px 8px 16px",
        gap: 2,
        position: "fixed",
        top: 64,
        boxShadow: "0px 25px 30px 0px rgba(65, 70, 79, 0.05)",
        zIndex: 99,
      }}
      ref={actionsRef}
    >
      {renderOrganisationsActions && (
        <OrganisationsActions isMultiple={false} />
      )}
      {!renderOrganisationsActions ||
      (selectedSingleOrganization && showSingleOrganizationTables) ? (
        <>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                width: "fit-content",
                textWrap: "nowrap",
                ...nawyTheme.nawy.typography.english.variants.body.lg.semibold,
              }}
            >
              Agents targets
            </Typography>

            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                  justifyContent: "end",
                }}
              >
                <Box sx={{ minWidth: "250px" }}>
                  <SingleSelectSearch
                    options={
                      users?.map((user) => ({
                        value: user.uid,
                        label: user.name,
                      })) || []
                    }
                    value={selectedUserUId}
                    label="Search by agent name"
                    onValueChange={(value) => handleUserChange(value)}
                    searchValue={usersSearchTerm}
                    isLoading={usersLoading}
                    onSearchValueChange={(value) => setUsersSearchTerm(value)}
                    sx={{ borderRadius: 2, height: 40 }}
                  />
                </Box>

                <FormControl sx={{ minWidth: 120 }}>
                  <Select
                    value={selectedYear?.toString() || ""}
                    onChange={(e) => handleYearChange(e.target.value as any)}
                    displayEmpty
                    sx={{ borderRadius: 2, height: 40 }}
                  >
                    {years.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 80 }}>
                  <Select
                    value={selectedQuarter.toString()}
                    onChange={(e) => handleQuarterChange(e.target.value as any)}
                    displayEmpty
                    sx={{ borderRadius: 2, height: 40 }}
                    disabled={!selectedYear}
                  >
                    {selectedYear &&
                      quarters
                        .filter((quarter) => {
                          if (selectedYear === currentYear) {
                            return quarter.id <= currentQuarter;
                          }
                          return true;
                        })
                        .map((quarter) => (
                          <MenuItem key={quarter.id} value={quarter.id}>
                            {quarter.name.toUpperCase()}
                          </MenuItem>
                        ))}
                  </Select>
                </FormControl>

                <Box sx={{ minWidth: "200px" }}>
                  <MonthlySummary disabled={isCurrentQuarter} />
                </Box>
                <Box sx={{ minWidth: "200px" }}>
                  <ViewQuarterlyTarget
                    handleChange={handleQuarterChange}
                    quarter={selectedQuarter}
                    year={selectedYear}
                  />
                </Box>
              </Box>
            )}

            {isMobile && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "space-between",
                }}
              >
                {isCurrentQuarter && <MonthlySummary />}

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CustomDropdown
                    triggerButton={triggerButton}
                    items={[
                      {
                        jsx: (
                          <OpenViewQuartelyTarget
                            handleChange={handleQuarterChange}
                            quarter={selectedQuarter}
                            year={selectedYear}
                          />
                        ),
                      },
                    ]}
                  />
                </Box>
              </Box>
            )}
          </Box>

          {isMobile && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth>
                  <Select
                    value={selectedYear?.toString() || ""}
                    onChange={(e) => handleYearChange(e.target.value as any)}
                    displayEmpty
                    sx={{ borderRadius: 1, height: 40 }}
                  >
                    {years.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <Select
                    value={selectedQuarter.toString()}
                    onChange={(e) => handleQuarterChange(e.target.value as any)}
                    displayEmpty
                    sx={{ borderRadius: 1, height: 40 }}
                    disabled={!selectedYear}
                  >
                    {selectedYear &&
                      quarters
                        .filter((quarter) => {
                          if (selectedYear === currentYear) {
                            return quarter.id <= currentQuarter;
                          }
                          return true;
                        })
                        .map((quarter) => (
                          <MenuItem key={quarter.id} value={quarter.id}>
                            {quarter.name.toUpperCase()}
                          </MenuItem>
                        ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ minWidth: "300px" }}>
                <SingleSelectSearch
                  options={
                    users?.map((user) => ({
                      value: user.uid,
                      label: user.name,
                    })) || []
                  }
                  value={selectedUserUId}
                  label="Search by agent name"
                  onValueChange={(value) => handleUserChange(value)}
                  searchValue={usersSearchTerm}
                  isLoading={usersLoading}
                  onSearchValueChange={(value) => {
                    setUsersSearchTerm(value);
                  }}
                  sx={{ borderRadius: 2, height: 40 }}
                />
              </Box>
            </Box>
          )}
        </>
      ) : (
        <></>
      )}
    </Box>
  );
};

export default TargetsActions;
